/**
 * Spec + slider values → everything the renderer needs, in data space.
 *
 * Deliberately pure and free of React: this is where the numerical work
 * happens, so it can be tested directly and memoized on (spec, params, theme).
 */

import type {
  FunctionSeries,
  HeatmapSeries,
  ParametricSeries,
  PlotSeries,
  PlotSpec,
  PointsSeries,
  VectorFieldSeries,
} from "../../shared/plot-spec.ts";
import { seriesVariables } from "../../shared/plot-spec.ts";
import {
  compileExpression,
  sampleFunction,
  sampleParametric,
  splitOnPoles,
  symbolsIn,
  type CompiledExpression,
  type Point,
  type Segments,
} from "./compile.ts";
import { autoRange } from "./scales.ts";
import { seriesColor, type ThemeMode } from "./palette.ts";

export type Range = readonly [number, number];

export interface CurveModel {
  readonly kind: "curve";
  readonly segments: Segments;
  readonly dashed: boolean;
  readonly fill: boolean;
}

export interface PointsModel {
  readonly kind: "points";
  readonly points: readonly Point[];
  readonly yError: readonly number[] | null;
  readonly showLine: boolean;
  readonly radius: number;
}

export interface Arrow {
  readonly x: number;
  readonly y: number;
  readonly u: number;
  readonly v: number;
  readonly magnitude: number;
}

export interface FieldModel {
  readonly kind: "field";
  readonly arrows: readonly Arrow[];
  readonly maxMagnitude: number;
  readonly normalize: boolean;
}

export interface HeatmapModel {
  readonly kind: "heatmap";
  /** Row-major, `resolution` × `resolution`, y ascending. */
  readonly values: readonly number[];
  readonly resolution: number;
  readonly min: number;
  readonly max: number;
  readonly colorScale: HeatmapSeries["colorScale"];
}

export interface FailedModel {
  readonly kind: "failed";
  readonly message: string;
}

export type SeriesBody =
  | CurveModel
  | PointsModel
  | FieldModel
  | HeatmapModel
  | FailedModel;

export interface ResolvedSeries {
  readonly key: string;
  readonly label: string;
  readonly color: string;
  readonly body: SeriesBody;
}

export interface PlotModel {
  readonly series: readonly ResolvedSeries[];
  readonly xDomain: Range;
  readonly yDomain: Range;
  /** Non-fatal problems worth surfacing under the figure. */
  readonly warnings: readonly string[];
}

export interface BuildOptions {
  readonly spec: PlotSpec;
  readonly params: Readonly<Record<string, number>>;
  readonly mode: ThemeMode;
  /** Box aspect (width / height), needed only when `equalAspect` is set. */
  readonly aspect: number;
}

export function buildPlotModel(options: BuildOptions): PlotModel {
  const { spec, params, mode } = options;
  const warnings: string[] = [];
  const scope = { ...params };

  const xDomain: Range = spec.xRange;
  const has2D = spec.series.some(
    (s) => s.type === "vectorField" || s.type === "heatmap",
  );

  // 2-D fields need a y-domain up front to sample over; square is the sane default.
  const provisionalY: Range =
    spec.yRange ?? (has2D ? symmetricLike(xDomain) : [-1, 1]);

  const resolved = spec.series.map((series, index) =>
    resolveSeries(series, index, {
      scope,
      mode,
      xDomain,
      yDomain: provisionalY,
      xLog: spec.xScale === "log",
      warnings,
    }),
  );

  const yDomain = spec.yRange ?? deriveYDomain(resolved, provisionalY, has2D);

  // Poles are only detectable once we know how tall the window is.
  const withBreaks = resolved.map((entry) =>
    entry.body.kind === "curve"
      ? {
          ...entry,
          body: { ...entry.body, segments: splitOnPoles(entry.body.segments, yDomain) },
        }
      : entry,
  );

  const [finalX, finalY] = spec.equalAspect
    ? equalize(xDomain, yDomain, options.aspect)
    : [xDomain, yDomain];

  return {
    series: withBreaks,
    xDomain: finalX,
    yDomain: finalY,
    warnings,
  };
}

/* -------------------------------------------------------------------------- */
/* Per-series resolution                                                       */
/* -------------------------------------------------------------------------- */

interface ResolveContext {
  readonly scope: Record<string, number>;
  readonly mode: ThemeMode;
  readonly xDomain: Range;
  readonly yDomain: Range;
  readonly xLog: boolean;
  readonly warnings: string[];
}

function resolveSeries(
  series: PlotSeries,
  index: number,
  context: ResolveContext,
): ResolvedSeries {
  const color = series.color ?? seriesColor(index, context.mode);
  const label = series.label ?? defaultLabel(series, index);
  const key = `series-${index}`;

  const undeclared = undeclaredSymbols(series, context.scope);
  if (undeclared.length > 0) {
    return {
      key,
      label,
      color,
      body: {
        kind: "failed",
        message: `undefined symbol${undeclared.length > 1 ? "s" : ""}: ${undeclared.join(", ")}`,
      },
    };
  }

  const body = buildBody(series, context);
  return { key, label, color, body };
}

function buildBody(series: PlotSeries, context: ResolveContext): SeriesBody {
  switch (series.type) {
    case "function":
      return buildFunction(series, context);
    case "parametric":
      return buildParametric(series, context);
    case "points":
      return buildPoints(series);
    case "vectorField":
      return buildField(series, context);
    case "heatmap":
      return buildHeatmap(series, context);
  }
}

function buildFunction(series: FunctionSeries, context: ResolveContext): SeriesBody {
  const compiled = compileExpression(series.expr);
  if (!compiled.ok) return { kind: "failed", message: compiled.error };

  const segments = sampleFunction(compiled.fn, series.variable, context.scope, {
    from: context.xDomain[0],
    to: context.xDomain[1],
    samples: series.samples,
    logSpacing: context.xLog,
  });

  if (isEmpty(segments)) {
    return { kind: "failed", message: "the expression is undefined everywhere on this range" };
  }

  return { kind: "curve", segments, dashed: series.dashed, fill: series.fill };
}

function buildParametric(
  series: ParametricSeries,
  context: ResolveContext,
): SeriesBody {
  const x = compileExpression(series.xExpr);
  if (!x.ok) return { kind: "failed", message: `x(t): ${x.error}` };
  const y = compileExpression(series.yExpr);
  if (!y.ok) return { kind: "failed", message: `y(t): ${y.error}` };

  const segments = sampleParametric(x.fn, y.fn, series.parameter, context.scope, {
    from: series.tRange[0],
    to: series.tRange[1],
    samples: series.samples,
  });

  if (isEmpty(segments)) {
    return { kind: "failed", message: "the curve is undefined over this parameter range" };
  }

  return { kind: "curve", segments, dashed: series.dashed, fill: false };
}

function buildPoints(series: PointsSeries): SeriesBody {
  const points = series.points.map(([x, y]) => ({ x, y }));
  const yError =
    series.yError && series.yError.length === points.length ? series.yError : null;

  return {
    kind: "points",
    points,
    yError,
    showLine: series.showLine,
    radius: series.radius,
  };
}

function buildField(series: VectorFieldSeries, context: ResolveContext): SeriesBody {
  const u = compileExpression(series.uExpr);
  if (!u.ok) return { kind: "failed", message: `u: ${u.error}` };
  const v = compileExpression(series.vExpr);
  if (!v.ok) return { kind: "failed", message: `v: ${v.error}` };

  const arrows: Arrow[] = [];
  let maxMagnitude = 0;
  const working: Record<string, number> = { ...context.scope };

  // Half-cell inset so arrows sit inside the frame rather than straddling it.
  for (let row = 0; row < series.density; row++) {
    for (let col = 0; col < series.density; col++) {
      const x = cellCentre(context.xDomain, col, series.density);
      const y = cellCentre(context.yDomain, row, series.density);

      working.x = x;
      working.y = y;
      const uValue = u.fn(working);
      const vValue = v.fn(working);
      if (!Number.isFinite(uValue) || !Number.isFinite(vValue)) continue;

      const magnitude = Math.hypot(uValue, vValue);
      if (magnitude > maxMagnitude) maxMagnitude = magnitude;
      arrows.push({ x, y, u: uValue, v: vValue, magnitude });
    }
  }

  if (arrows.length === 0) {
    return { kind: "failed", message: "the field is undefined across this region" };
  }

  return { kind: "field", arrows, maxMagnitude, normalize: series.normalize };
}

function buildHeatmap(series: HeatmapSeries, context: ResolveContext): SeriesBody {
  const compiled = compileExpression(series.expr);
  if (!compiled.ok) return { kind: "failed", message: compiled.error };

  const n = series.resolution;
  const values = new Array<number>(n * n);
  const working: Record<string, number> = { ...context.scope };

  const finite: number[] = [];

  for (let row = 0; row < n; row++) {
    const y = cellCentre(context.yDomain, row, n);
    for (let col = 0; col < n; col++) {
      working.x = cellCentre(context.xDomain, col, n);
      working.y = y;
      const value = compiled.fn(working);
      values[row * n + col] = value;
      if (Number.isFinite(value)) finite.push(value);
    }
  }

  if (finite.length === 0) {
    return { kind: "failed", message: "the field is undefined across this region" };
  }

  // Clip to percentiles: a single 1/r singularity would otherwise wash out
  // the entire map to one flat colour.
  finite.sort((a, b) => a - b);
  const min = quantile(finite, 0.02);
  const max = quantile(finite, 0.98);

  return {
    kind: "heatmap",
    values,
    resolution: n,
    min,
    max: max > min ? max : min + 1,
    colorScale: series.colorScale,
  };
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function undeclaredSymbols(
  series: PlotSeries,
  scope: Readonly<Record<string, number>>,
): readonly string[] {
  const allowed = new Set([...seriesVariables(series), ...Object.keys(scope)]);
  const expressions = expressionsOf(series);

  const missing = new Set<string>();
  for (const expr of expressions) {
    for (const symbol of symbolsIn(expr)) {
      if (!allowed.has(symbol)) missing.add(symbol);
    }
  }
  return [...missing];
}

function expressionsOf(series: PlotSeries): readonly string[] {
  switch (series.type) {
    case "function":
    case "heatmap":
      return [series.expr];
    case "parametric":
      return [series.xExpr, series.yExpr];
    case "vectorField":
      return [series.uExpr, series.vExpr];
    case "points":
      return [];
  }
}

function defaultLabel(series: PlotSeries, index: number): string {
  switch (series.type) {
    case "function":
      return series.expr;
    case "parametric":
      return `(${series.xExpr}, ${series.yExpr})`;
    case "vectorField":
      return "vector field";
    case "heatmap":
      return series.expr;
    case "points":
      return `data ${index + 1}`;
  }
}

function deriveYDomain(
  series: readonly ResolvedSeries[],
  provisional: Range,
  has2D: boolean,
): Range {
  if (has2D) return provisional;

  const segments: Point[][] = [];
  for (const entry of series) {
    const body = entry.body;

    if (body.kind === "curve") {
      segments.push(...body.segments.map((s) => [...s]));
      continue;
    }

    if (body.kind === "points") {
      segments.push([...body.points]);

      const errors = body.yError;
      if (errors) {
        // Keep error bars inside the frame, not clipped at the top.
        segments.push(
          body.points.map((p, i) => ({ x: p.x, y: p.y + (errors[i] ?? 0) })),
          body.points.map((p, i) => ({ x: p.x, y: p.y - (errors[i] ?? 0) })),
        );
      }
    }
  }

  return segments.length === 0 ? provisional : autoRange(segments, provisional);
}

/** Widen the narrower axis so one x-unit equals one y-unit on screen. */
function equalize(x: Range, y: Range, aspect: number): readonly [Range, Range] {
  const xSpan = x[1] - x[0];
  const ySpan = y[1] - y[0];
  const target = ySpan * aspect;

  if (Math.abs(target - xSpan) < 1e-9) return [x, y];

  if (target > xSpan) {
    const centre = (x[0] + x[1]) / 2;
    return [[centre - target / 2, centre + target / 2], y];
  }

  const neededY = xSpan / aspect;
  const centre = (y[0] + y[1]) / 2;
  return [x, [centre - neededY / 2, centre + neededY / 2]];
}

function symmetricLike(range: Range): Range {
  const span = range[1] - range[0];
  const centre = (range[0] + range[1]) / 2;
  return [centre - span / 2, centre + span / 2];
}

const cellCentre = (range: Range, index: number, count: number): number =>
  range[0] + ((index + 0.5) / count) * (range[1] - range[0]);

function quantile(sorted: readonly number[], fraction: number): number {
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.round((sorted.length - 1) * fraction)),
  );
  return sorted[index]!;
}

const isEmpty = (segments: Segments): boolean =>
  segments.every((segment) => segment.length < 2);
